import { useMemo } from 'react';

// Utils
import cn from '../../../../utils/cn';
import { getContrastTextColor } from '../../Fields/ColorField/useColorField';

// Styles
import styles from './ColorCell.module.css';
import commonStyles from '../TableCellCommon.module.css';

/**
 * Color cell component for displaying color values in table cells
 * @param {object} props
 * @param {string} [props.value] - Color value in hex format (e.g., "#FF0000")
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.placeholder='-'] - Placeholder text when empty
 * @param {Align} [props.align='center'] - Text alignment
 * @param {VerticalAlign} [props.verticalAlign='middle'] - Vertical alignment
 * @param {boolean} [props.bold=false] - Text in bold
 * @param {boolean} [props.italic=false] - Text in italic
 * @param {boolean} [props.strikeThrough=false] - Text with strike-through
 * @param {boolean} [props.underline=false] - Text underlined
 * @returns {JSX.Element}
 */
function ColorCell({
  value,
  className = '',
  placeholder = '-',
  align = 'center',
  verticalAlign = 'middle',
  bold = false,
  italic = false,
  strikeThrough = false,
  underline = false,
}) {
  const isEmpty = !value || value === '';

  const textColor = useMemo(
    () => (isEmpty ? 'inherit' : getContrastTextColor(value)),
    [value, isEmpty],
  );

  const pickedColor = useMemo(
    () => (isEmpty ? 'transparent' : value),
    [value, isEmpty],
  );

  const cellClassName = cn([
    styles['color-cell'],
    className,
    bold && commonStyles['cell-bold'],
    italic && commonStyles['cell-italic'],
    strikeThrough && commonStyles['cell-strike-through'],
    underline && commonStyles['cell-underline'],
    align && commonStyles[`cell-align-${align}`],
    verticalAlign && commonStyles[`cell-vertical-align-${verticalAlign}`],
  ]);

  if (isEmpty) {
    return (
      <div className={cellClassName}>
        {placeholder ? (
          <div className={styles['color-cell-display']}>
            {placeholder}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className={cellClassName}>
      <div
        className={styles['color-cell-display']}
        style={{
          backgroundColor: pickedColor,
          color: textColor,
        }}
      >
        {value}
      </div>
    </div>
  );
}

export default ColorCell;
